import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@corpuscomplete/web/components/link";
import { createId } from "@corpuscomplete/core/utils";
import { MealPlanningSession } from "@corpuscomplete/core/nutrition/meal-planning-session";
import { MealPlanningSessionList } from "@corpuscomplete/core-ui/nutrition/meal-planning-session/list";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@corpuscomplete/web-ui/components/card";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { ScopeBreadcrumb } from "@corpuscomplete/web/modules/common/components/scope-breadcrumb";
import { Scope } from "@corpuscomplete/core/common/scope";

export function MealPlanningSessionsPage({ scope }: { scope: Scope.Info }) {
  const { userId } = useAuth();
  const scopeId = Scope.id(scope);

  return (
    <MealPlanningSessionList.DataProvider userId={userId} scopeId={scopeId}>
      <MealPlanningSessionsContent scope={scope} userId={userId} />
    </MealPlanningSessionList.DataProvider>
  );
}

function MealPlanningSessionsContent({ scope, userId }: { scope: Scope.Info; userId: string }) {
  const navigate = useNavigate();
  const sessions = MealPlanningSessionList.useSessions();
  const { isLoading } = MealPlanningSessionList.useLoadingState();
  const createState = MealPlanningSessionList.useCreateState();
  const { setDateStart, setDateEnd, setSelectedMealScheduleId, toggleMember } = createState;
  const { create } = MealPlanningSessionList.useMutations();
  const [error, setError] = useState<string | null>(null);
  const invalidDateRange = createState.errors.some((issue) => issue.field === "dateEnd");

  const createSession = () => {
    const id = createId();
    const firstError = createState.errors[0];
    if (firstError) {
      setError(firstError.message);
      return;
    }
    setError(null);
    if (!createState.selectedMealScheduleId) {
      setError("Select a meal schedule.");
      return;
    }
    const result = create({
      id,
      foodCollectionId: createId(),
      dateStart: createState.dateStart,
      dateEnd: createState.dateEnd,
      timeZone: Temporal.Now.timeZoneId(),
      defaultMealScheduleId: createState.selectedMealScheduleId,
      memberUserIds: [...createState.selectedMemberUserIds],
    });
    void result.server
      .then((serverResult) => {
        if (serverResult.type === "error") {
          setError(serverResult.error.message);
          return;
        }
        void navigate({
          to: "/meal-planning-sessions/$id",
          params: { id },
        });
      })
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : "Failed to create session"),
      );
  };

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <div className="mr-auto min-w-0">
            <ScopeBreadcrumb
              crumbs={[{ label: "Meal Planning Sessions", current: true }]}
              scope={scope}
              scopeBehavior="switchable"
              onScopeChange={(nextScopeId) =>
                nextScopeId === userId
                  ? void navigate({ to: "/personal/meal-planning-sessions" })
                  : void navigate({
                      to: "/spaces/$spaceId/meal-planning-sessions",
                      params: { spaceId: nextScopeId },
                    })
              }
            />
          </div>
        </PageHeader>
        <PageContent className="px-4 py-4 sm:px-6">
          <div className="mx-auto grid w-full max-w-5xl gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
            <Card>
              <CardHeader>
                <CardTitle>New Session</CardTitle>
                <CardDescription>Start with all space members selected by default.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {error && <Text className="text-destructive">{error}</Text>}
                <label className="flex flex-col gap-1 text-sm">
                  Start date
                  <Input
                    type="date"
                    value={createState.dateStart.toString()}
                    onChange={(event) => setDateStart(Temporal.PlainDate.from(event.target.value))}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  End date
                  <Input
                    type="date"
                    value={createState.dateEnd.toString()}
                    onChange={(event) => setDateEnd(Temporal.PlainDate.from(event.target.value))}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  Meal schedule
                  <select
                    className="h-9 rounded-md border bg-background px-3 text-sm"
                    value={createState.selectedMealScheduleId ?? ""}
                    onChange={(event) => setSelectedMealScheduleId(event.target.value)}
                  >
                    {createState.scheduleOptions.map((schedule) => (
                      <option key={schedule.id} value={schedule.id}>
                        {schedule.name}
                      </option>
                    ))}
                  </select>
                </label>
                {createState.memberOptions.length > 0 && (
                  <div className="flex flex-col gap-2 text-sm">
                    <Text variant="sm">Members</Text>
                    {createState.memberOptions.map((member) => (
                      <label key={member.userId} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={createState.selectedMemberUserIds.includes(member.userId)}
                          onChange={() => toggleMember(member.userId)}
                        />
                        <span>{member.label}</span>
                      </label>
                    ))}
                  </div>
                )}
                <Button onClick={createSession}>Create Session</Button>
                {invalidDateRange && (
                  <Text variant="sm" className="text-destructive">
                    End date must be same as or after start date.
                  </Text>
                )}
              </CardContent>
            </Card>
            <div className="flex min-w-0 flex-col gap-3">
              {isLoading ? null : sessions.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center">
                    <Text>No meal-planning sessions yet.</Text>
                  </CardContent>
                </Card>
              ) : (
                sessions.map((session) => <SessionCard key={session.id} session={session} />)
              )}
            </div>
          </div>
        </PageContent>
      </PageBody>
    </Page>
  );
}

function SessionCard({ session }: { session: MealPlanningSession.Preview }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex min-w-0 items-center justify-between gap-3">
          <div className="min-w-0">
            <CardTitle>
              {session.dateStart.toString()} to {session.dateEnd.toString()}
            </CardTitle>
            <CardDescription>{session.group?.members.length ?? 0} members</CardDescription>
          </div>
          <Link to="/meal-planning-sessions/$id" params={{ id: session.id }}>
            <Button variant="outline">Open</Button>
          </Link>
        </div>
      </CardHeader>
    </Card>
  );
}
